import React from 'react';
import { FiX } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import {
  getMapPinLabel,
  getPropertyStatusLabel,
  formatStateInCityFromIds,
} from '../../utils/locationFormat';
import './PropertyMapLocationsCell.css';

export const resolvePropertyThumb = (thumbnail) => {
  const t = typeof thumbnail === 'string' ? thumbnail.trim() : '';
  if (!t) return 'https://placehold.co/120x90?text=No+Image';
  if (/^https?:\/\//i.test(t)) return t;
  const base = API_URL.replace('/api', '');
  if (t.startsWith('uploads/')) return `${base}/${t}`;
  if (t.startsWith('/uploads/')) return `${base}${t}`;
  return `${base}/uploads/${t}`;
};

const formatValue = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
};

const HIDDEN_KEYS = new Set(['_id', '__v']);

const PropertyLocationDetailModal = ({ property, onClose, states = [], cities = [], subRegisters = [] }) => {
  if (!property) return null;

  const thumb = resolvePropertyThumb(property.thumbnail);
  const mapLabel = getMapPinLabel(property);
  const statusLabel = getPropertyStatusLabel(property.status_id);
  const regionLabel = formatStateInCityFromIds(property.state_id, property.city_id, states, cities);
  const subId = Number(property.sub_register_id ?? property.extra_fields?.sub_register_id);
  const subName = subRegisters.find((s) => Number(s.id) === subId)?.sub_name;

  const detailEntries = Object.entries(property)
    .filter(([k]) => !HIDDEN_KEYS.has(k))
    .sort(([a], [b]) => a.localeCompare(b));

  const hasCoords = Number.isFinite(Number(property.latitude)) && Number.isFinite(Number(property.longitude));

  return (
    <div className="admin-modal-overlay animate-fade-in" onClick={onClose}>
      <div className="admin-modal pml-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Property location & details</h2>
          <button type="button" className="btn-icon" onClick={onClose}><FiX /></button>
        </div>
        <div className="pml-modal-body">
          <div className="pml-modal-top">
            <img src={thumb} alt="" className="pml-modal-thumb" onError={(e) => { e.target.src = 'https://placehold.co/120x90?text=No+Image'; }} />
            <div className="pml-modal-summary">
              <p><strong>ID:</strong> #{property.property_id || property.id}</p>
              <p><strong>Map location:</strong> {mapLabel}</p>
              <p><strong>Region:</strong> {regionLabel || '—'}</p>
              {subName && <p><strong>Sub-register:</strong> {subName}</p>}
              {hasCoords && (
                <p><strong>Coordinates:</strong> {Number(property.latitude).toFixed(5)}, {Number(property.longitude).toFixed(5)}</p>
              )}
              <p><strong>Created by:</strong> {property.creator_name || 'Unknown'}{property.creator_phone ? ` · ${property.creator_phone}` : ''}</p>
              <p><strong>Status:</strong> <span className={`pml-status pml-status-${property.status_id}`}>{statusLabel}</span></p>
              {hasCoords && (
                <a
                  className="pml-map-link"
                  href={`https://www.openstreetmap.org/?mlat=${property.latitude}&mlon=${property.longitude}#map=16/${property.latitude}/${property.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in map
                </a>
              )}
            </div>
          </div>

          <h3 className="pml-modal-section-title">All submitted data</h3>
          <div className="pml-detail-grid">
            {detailEntries.map(([key, val]) => (
              <div key={key} className="pml-detail-row">
                <span className="pml-detail-key">{key}</span>
                <pre className="pml-detail-val">{formatValue(val)}</pre>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyLocationDetailModal;
